import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, ExternalLink } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { productCategories } from '../data/products';
import PageHeader from '../components/PageHeader';

const ProductsPage = () => {
  return (
    <div className="min-h-screen pt-20 bg-white">
      {/* Hero */}
      <PageHeader 
        title="Our Products"
        subtitle="Innovative solutions across mobile apps, games, utilities, AI, and enterprise platforms"
        backgroundImage="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2015"
      />

      {/* Product Categories */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {productCategories.map((category, index) => {
              const IconComponent = LucideIcons[category.icon] || LucideIcons.Package;
              
              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link to={`/products/${category.slug}`}>
                    <Card className="h-full hover:shadow-xl transition-all duration-300 group border border-gray-100 hover:border-[#320547]/30 overflow-hidden">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <CardContent className="p-6">
                        <div className="w-14 h-14 bg-gradient-to-br from-[#320547] to-[#95228A] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                          <IconComponent className="text-white" size={28} />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#320547] transition-colors">
                          {category.name}
                        </h3>
                        <p className="text-gray-500 mb-4">{category.description}</p>
                        <div className="flex items-center text-[#320547] font-semibold group-hover:underline">
                          Explore {category.products.length} Products 
                          <ExternalLink className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Need a Custom Solution?
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Let's discuss your unique requirements and build a tailored solution
            </p>
            <Button asChild size="lg">
              <Link to="/contact">Get in Touch</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const ProductCategory = () => {
  const { slug } = useParams();
  const category = productCategories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-white">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Category Not Found</h2>
          <Button asChild>
            <Link to="/products">Back to Products</Link>
          </Button>
        </div>
      </div>
    );
  }

  const IconComponent = LucideIcons[category.icon] || LucideIcons.Package;

  return (
    <div className="min-h-screen pt-20 bg-white">
      {/* Hero */}
      <PageHeader 
        title={category.name}
        subtitle={category.description}
        backgroundImage={category.image}
      />
      <div className="bg-gray-50 py-4 border-b border-gray-100">
        <div className="container mx-auto px-4">
          <Button
            asChild
            variant="ghost"
            className="text-gray-600 hover:text-[#320547]"
          >
            <Link to="/products">
              <ArrowLeft className="mr-2" size={18} /> Back to Products
            </Link>
          </Button>
        </div>
      </div>

      {/* Products */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {category.products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid md:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'md:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="rounded-2xl shadow-xl w-full h-96 object-cover"
                  />
                </div>
                <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{product.name}</h2>
                  <p className="text-gray-600 mb-6 text-lg">{product.description}</p>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Key Features</h3>
                  <ul className="space-y-3 mb-6">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <CheckCircle className="text-[#320547] flex-shrink-0 mt-1" size={20} />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Technologies</h3>
                    <div className="flex flex-wrap gap-2">
                      {product.technologies.map((tech, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-gray-100 text-gray-700">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <Button>
                    Learn More
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Interested in {category.name}?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Get in touch to discuss how our products can benefit your business
          </p>
          <Button asChild size="lg">
            <Link to="/contact">Contact Us</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export { ProductsPage, ProductCategory };
